import React from "react";
import { getExecutionPreview, createExecution } from "./api/executionClient";
import { health as snHealth, demandLink, pushDemand } from "../../api/serviceNowClient";

const cx = (...c) => c.filter(Boolean).join(" ");

function Card({ title, subtitle, right, children, className }) {
  return (
    <section className={cx("rounded-2xl border border-gray-200 bg-white shadow-sm", className)}>
      {(title || subtitle || right) && (
        <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
          {right}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

function Pill({ children, tone = "gray" }) {
  const map = {
    gray: "bg-gray-100 text-gray-800",
    green: "bg-green-100 text-green-800",
    blue: "bg-blue-100 text-blue-800",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-800",
  };
  return (
    <span className={cx("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium", map[tone])}>
      {children}
    </span>
  );
}

/**
 * Tenant status banner.
 *
 * This exists because the handoff has three distinct ways to be unusable and each
 * needs a different fix: not configured, configured but switched off, and configured
 * but rejected by the instance. The previous version of this screen surfaced none of
 * them — its client caught every failure and reported success with a fabricated
 * https://snow.example/... link.
 */
function ServiceNowStatus({ status, error }) {
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
        Could not reach the AIEA ServiceNow endpoint: {error}
      </div>
    );
  }
  if (!status) {
    return <div className="text-sm text-gray-500">Checking ServiceNow…</div>;
  }

  const { configured, use_real_sn: useReal, reachable, demand_table_ok: demandOk } = status;

  if (!configured) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <div className="font-medium">ServiceNow is not configured.</div>
        Set SN_INSTANCE_URL, SN_USER and SN_PASSWORD_FILE in backend/AIEA/backend/.env
        — see .env.servicenow.example.
      </div>
    );
  }
  if (!useReal) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <div className="font-medium">ServiceNow calls are disabled.</div>
        {status.instance_url} is configured, but USE_REAL_SN is false.
      </div>
    );
  }
  if (!reachable || !demandOk) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
        <div className="font-medium">
          ServiceNow rejected the connection{status.status ? ` (HTTP ${status.status})` : ""}.
        </div>
        <div className="mt-1 break-words">{status.error || "Unknown error"}</div>
        {status.status === 401 && (
          <div className="mt-2">
            A 401 usually means the instance password was reset — replace the contents
            of backend/AIEA/.sn-creds and retry.
          </div>
        )}
        {status.status === 403 && (
          <div className="mt-2">
            A 403 means the service account is missing a role for {status.tables?.demand}.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1 text-sm text-gray-700">
      <div className="flex items-center gap-2">
        <Pill tone="green">connected</Pill>
        <span className="font-medium">{status.instance_url}</span>
      </div>
      <div className="text-xs text-gray-500">
        as {status.user} • demand table {status.tables?.demand}
        {status.project_table_ok === false && (
          <span className="text-amber-700">
            {" "}• project table {status.tables?.project} unavailable (plugin missing?)
          </span>
        )}
      </div>
    </div>
  );
}

export default function ExecutionPreview({ demandId }) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [status, setStatus] = React.useState(null);
  const [statusError, setStatusError] = React.useState("");
  const [link, setLink] = React.useState(null);
  const [busy, setBusy] = React.useState(null);       // 'create' | 'push' | null
  const [outcome, setOutcome] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!demandId) return;
    setError("");
    setLoading(true);
    try {
      const [p, l] = await Promise.all([getExecutionPreview(demandId), demandLink(demandId)]);
      setPreview(p);
      setLink(l);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [demandId]);

  React.useEffect(() => { load(); }, [load]);

  // Health is fetched separately so an unreachable tenant doesn't stop the preview
  // — which is pure AIEA data — from rendering.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const h = await snHealth();
        if (!cancelled) setStatus(h);
      } catch (e) {
        if (!cancelled) setStatusError(e.message || String(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const canCreate = Boolean(status?.configured && status?.use_real_sn && status?.reachable);

  const run = async (kind, fn) => {
    setBusy(kind);
    setOutcome(null);
    try {
      const res = await fn();
      setOutcome({ ok: true, ...res });
      await load();
    } catch (e) {
      // Each status code has a different remedy, so name it rather than showing a
      // bare message.
      setOutcome({
        ok: false,
        message: e.message || String(e),
        hint:
          e.status === 409 ? "The demand needs a TAB approval before it can be handed off."
          : e.status === 503 ? "ServiceNow is unconfigured, or USE_REAL_SN is false."
          : e.status === 501 ? "That execution tool has no integration."
          : e.status === 502 ? "ServiceNow rejected the call — check /api/servicenow/health."
          : undefined,
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-4">
      <div className="sticky top-0 z-10 -mx-4 mb-3 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="mx-4 flex items-center justify-between py-3">
          <p className="text-xs text-gray-500">Demand {demandId}</p>
          <div className="flex items-center gap-2">
            <Pill tone="blue">{loading ? "Loading…" : preview?.tool || "servicenow"}</Pill>
            {link?.linked && <Pill tone="green">linked {link.snow_number}</Pill>}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">{error}</div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card
          className="lg:col-span-2"
          title="Objects to be created"
          subtitle="Derived from this demand's TAB decision and conditions"
        >
          {loading || !preview ? (
            <div className="text-sm text-gray-500">Loading preview…</div>
          ) : (
            <>
              {preview.alreadyCreated && (
                <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                  A ServiceNow project already exists for this demand
                  {preview.existing?.projectId ? ` (${preview.existing.projectId})` : ""}.
                  Creating again is blocked so a retry can't produce a second project.
                  {preview.existing?.url && (
                    <> <a className="underline" href={preview.existing.url} target="_blank" rel="noreferrer">Open it</a>.</>
                  )}
                </div>
              )}
              <div className="rounded-lg border" style={{ overflowX: "auto" }}>
                <table className="min-w-full text-sm" style={{ width: "100%" }}>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">ServiceNow table</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.willCreate?.map((row, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-2">{row.type}</td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-600">{row.table}</td>
                        <td className="px-3 py-2">{row.summary}</td>
                      </tr>
                    ))}
                    {!preview.willCreate?.length && (
                      <tr><td className="px-3 py-4 text-gray-500" colSpan={3}>Nothing to create.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                One project, plus one task per TAB approval condition. Conditions are
                set on the TAB Decision screen.
              </p>
            </>
          )}
        </Card>

        <div className="space-y-4">
          <Card title="ServiceNow" subtitle="Tenant status">
            <ServiceNowStatus status={status} error={statusError} />

            {link && (
              <div className="mt-3 space-y-1 border-t border-gray-100 pt-3 text-sm">
                <div><span className="font-medium">Source:</span> {link.source_system || "AIEA"}</div>
                <div>
                  <span className="font-medium">Demand record:</span>{" "}
                  {link.linked ? (
                    <a className="text-indigo-600 hover:underline" href={link.demandUrl}
                       target="_blank" rel="noreferrer">{link.snow_number}</a>
                  ) : <span className="text-gray-500">not linked yet</span>}
                </div>
                {link.snow_project_id && (
                  <div>
                    <span className="font-medium">Project:</span>{" "}
                    <a className="text-indigo-600 hover:underline" href={link.projectUrl}
                       target="_blank" rel="noreferrer">{link.snow_project_id}</a>
                  </div>
                )}
                {link.snow_sync_error && (
                  <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                    Last sync error: {link.snow_sync_error}
                  </div>
                )}
              </div>
            )}
          </Card>

          <Card title="Handoff" subtitle="Creates records in ServiceNow">
            <div className="space-y-2">
              <button
                onClick={() => run("push", () => pushDemand(demandId))}
                disabled={!canCreate || busy !== null}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                title="Create or update this demand's ServiceNow demand record"
              >
                {busy === "push" ? "Syncing…" : "Sync demand to ServiceNow"}
              </button>
              <button
                onClick={() => run("create", () => createExecution(demandId, { tool: "servicenow" }))}
                disabled={!canCreate || busy !== null || preview?.alreadyCreated}
                className="w-full rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {busy === "create" ? "Creating…"
                  : preview?.alreadyCreated ? "Already created"
                  : "Create project & condition tasks"}
              </button>
              {!canCreate && (
                <p className="text-xs text-gray-500">
                  Both actions stay disabled until the tenant check above passes.
                </p>
              )}
            </div>

            {outcome && (
              <div className={cx(
                "mt-3 rounded-md border p-3 text-sm",
                outcome.ok ? "border-green-200 bg-green-50 text-green-900"
                           : "border-red-200 bg-red-50 text-red-900"
              )}>
                <div className="mb-1 font-medium">{outcome.ok ? "Done" : "Failed"}</div>
                <div className="mb-2 break-words">{outcome.message || ""}</div>
                {outcome.hint && <div className="mb-2 text-xs italic">{outcome.hint}</div>}
                {outcome.ok && outcome.links && (
                  <ul className="space-y-1">
                    {Object.entries(outcome.links).filter(([, v]) => v).map(([k, v]) => (
                      <li key={k}>
                        <span className="font-medium">{k}: </span>
                        <a className="text-indigo-600 hover:underline" href={v}
                           target="_blank" rel="noreferrer">{v}</a>
                      </li>
                    ))}
                  </ul>
                )}
                {outcome.ok && outcome.url && (
                  <a className="text-indigo-600 hover:underline" href={outcome.url}
                     target="_blank" rel="noreferrer">{outcome.number || outcome.url}</a>
                )}
                {outcome.taskErrors?.length > 0 && (
                  <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                    The project was created, but {outcome.taskErrors.length} condition
                    task(s) failed: {outcome.taskErrors.map((t) => t.condition).join(", ")}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
